import { useState, useEffect } from "react";
import { Plus, Star, Trash2, Edit2, Eye, EyeOff } from "lucide-react";
import { testimonialService } from "@/services/firebase";
import { CloudinaryImageField } from "@/components/admin/CloudinaryImageField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ConfirmationModal } from "@/components/shared/ConfirmationModal";
import toast from "react-hot-toast";
import type { Testimonial } from "@/types";

export function TestimonialsAdminPage() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [editing, setEditing] = useState<Testimonial | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [testimonialToDelete, setTestimonialToDelete] = useState<Testimonial | null>(null);

  useEffect(() => {
    testimonialService.getAll().then(setTestimonials);
  }, []);

  const handleSave = async () => {
    if (!editing) return;
    try {
      if (editing.id && testimonials.find((t) => t.id === editing.id)) {
        await testimonialService.update(editing.id, editing);
        toast.success("Témoignage mis à jour");
      } else {
        await testimonialService.create(editing);
        toast.success("Témoignage créé");
      }
      setEditing(null);
      setIsCreating(false);
      testimonialService.getAll().then(setTestimonials);
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleDelete = (testimonial: Testimonial) => {
    setTestimonialToDelete(testimonial);
  };

  const confirmDelete = async () => {
    if (!testimonialToDelete) return;
    try {
      await testimonialService.delete(testimonialToDelete.id);
      toast.success("Témoignage supprimé");
      setTestimonials((prev) => prev.filter((t) => t.id !== testimonialToDelete.id));
      setTestimonialToDelete(null);
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleToggleActive = async (testimonial: Testimonial) => {
    try {
      await testimonialService.toggleActive(testimonial.id, testimonial.active ?? true);
      toast.success(testimonial.active ? "Témoignage désactivé" : "Témoignage activé");
      testimonialService.getAll().then(setTestimonials);
    } catch {
      toast.error("Erreur");
    }
  };

  const newTestimonial: Testimonial = {
    id: "",
    name: "",
    role: "",
    location: "",
    rating: 5,
    text: "",
    type: "client",
    verified: true,
    featured: false,
    active: true,
    order: testimonials.length + 1,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark">Gestion des Témoignages</h1>
          <p className="text-gray-500 text-sm">{testimonials.length} témoignages</p>
        </div>
        <Button
          onClick={() => {
            setEditing(newTestimonial);
            setIsCreating(true);
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter
        </Button>
      </div>

      {/* Edit Form */}
      {(editing || isCreating) && (
        <Card>
          <CardContent className="p-6 space-y-4">
            <h3 className="font-bold text-lg">
              {isCreating ? "Nouveau témoignage" : "Modifier le témoignage"}
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input
                label="Nom"
                value={editing?.name || ""}
                onChange={(e) => setEditing((prev) => prev ? { ...prev, name: e.target.value } : null)}
              />
              <Input
                label="Rôle"
                value={editing?.role || ""}
                onChange={(e) => setEditing((prev) => prev ? { ...prev, role: e.target.value } : null)}
              />
              <Input
                label="Localisation"
                value={editing?.location || ""}
                onChange={(e) => setEditing((prev) => prev ? { ...prev, location: e.target.value } : null)}
              />
              <Input
                label="Note (1-5)"
                type="number"
                min={1}
                max={5}
                value={editing?.rating || 5}
                onChange={(e) => setEditing((prev) => prev ? { ...prev, rating: parseInt(e.target.value) } : null)}
              />
            </div>
            <Textarea
              label="Texte du témoignage"
              value={editing?.text || ""}
              onChange={(e) => setEditing((prev) => prev ? { ...prev, text: e.target.value } : null)}
              rows={4}
            />
            <CloudinaryImageField
              label="Avatar"
              value={editing?.avatar}
              folder="testimonials"
              onChange={(avatar) => setEditing((prev) => prev ? { ...prev, avatar } : null)}
              hint="Avatar optionnel affiché sur la vitrine."
            />
            <div className="flex gap-3">
              <Button onClick={handleSave}>Sauvegarder</Button>
              <Button
                variant="outline"
                onClick={() => {
                  setEditing(null);
                  setIsCreating(false);
                }}
              >
                Annuler
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* List */}
      <div className="grid gap-4">
        {testimonials.map((t) => (
          <Card key={t.id} className={!t.active ? "opacity-50" : ""}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-bold text-dark">{t.name}</h4>
                    <Badge variant={t.type === "client" ? "secondary" : "default"}>{t.type}</Badge>
                    {t.verified && <Badge variant="success">Vérifié</Badge>}
                    {!t.active && <Badge variant="outline">Inactif</Badge>}
                  </div>
                  <p className="text-sm text-gray-500 mb-2">
                    {t.role} à {t.location}
                  </p>
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${i < t.rating ? "text-amber-400 fill-amber-400" : "text-gray-200"}`}
                      />
                    ))}
                  </div>
                  <p className="text-gray-600 text-sm italic">"{t.text}"</p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleToggleActive(t)}
                    className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                    title={t.active ? "Désactiver" : "Activer"}
                  >
                    {t.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => {
                      setEditing({ ...t }); // injecte l'encart sélectionné dans l'état
                      setIsCreating(false); // spécifie qu'on est en modification
                    }}
                    className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Modifier"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(t)}
                    className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <ConfirmationModal
        isOpen={!!testimonialToDelete}
        onClose={() => setTestimonialToDelete(null)}
        onConfirm={confirmDelete}
        title="Supprimer le témoignage"
        message={`Êtes-vous sûr de vouloir supprimer le témoignage de ${testimonialToDelete?.name || "ce client"} ?`}
      />
    </div>

    
  );
}
